import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, CreditCard, Plus, Minus, LifeBuoy, ReceiptText, Pencil } from "lucide-react";

export const Card = ({ title, icon: Icon, children, testid }) => (
  <div className="rounded-3xl border border-[#262626] bg-[#101010] p-6" data-testid={testid}>
    <div className="flex items-center gap-3">
      <Icon className="h-5 w-5 text-[#D4A437]" />
      <h3 className="font-display text-lg font-semibold">{title}</h3>
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

export const AddressCard = ({ customer, onEdit }) => (
  <Card title="Delivery address" icon={MapPin} testid="address-card">
    <address className="not-italic text-[15px] leading-relaxed text-[#F7F5F0]/85" data-testid="address-display">
      {customer.address.line1}<br />
      {customer.address.line2 && <>{customer.address.line2}<br /></>}
      {customer.address.town}<br />
      {customer.address.postcode}
    </address>
    <p className="mt-3 text-sm text-[#A1A1AA]"><span className="text-[#F7F5F0]/60">Instructions:</span> {customer.instructions || "None"}</p>
    <button onClick={onEdit} className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#FF6A00] hover:underline" data-testid="address-edit-button">
      <Pencil className="h-4 w-4" /> Edit address & instructions
    </button>
  </Card>
);

export const BillingCard = ({ onInvoices }) => (
  <Card title="Billing" icon={CreditCard} testid="billing-card">
    <p className="text-sm text-[#A1A1AA]">Payments are handled securely by our payment provider. We never see or store your card details.</p>
    <div className="mt-4 grid gap-2">
      <button className="lc-btn-ghost !py-3" onClick={() => toast("In your real account you can update your card on our payment provider's secure page.")} data-testid="billing-portal-button">
        Update payment details
      </button>
      <button className="inline-flex items-center justify-center gap-2 py-2 text-sm font-semibold text-[#FF6A00] hover:underline" onClick={onInvoices} data-testid="billing-invoices-button">
        <ReceiptText className="h-4 w-4" /> View invoices
      </button>
    </div>
  </Card>
);

export const ExtrasCard = () => {
  const [qty, setQty] = useState(1);
  return (
    <Card title="Order extra logs" icon={Plus} testid="extras-card">
      <p className="text-sm text-[#A1A1AA]">Add extra bags of kiln-dried logs to your next delivery. Add-on prices are set by the WeeLogRun team.</p>
      <div className="mt-4 flex items-center justify-between rounded-2xl border border-[#262626] p-2">
        <button aria-label="Decrease quantity" className="grid h-10 w-10 place-items-center rounded-xl hover:bg-white/5" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="extras-decrease"><Minus className="h-4 w-4" /></button>
        <span className="font-display text-xl font-bold" aria-live="polite" data-testid="extras-qty">{qty} bag{qty > 1 && "s"}</span>
        <button aria-label="Increase quantity" className="grid h-10 w-10 place-items-center rounded-xl hover:bg-white/5" onClick={() => setQty(Math.min(6, qty + 1))} data-testid="extras-increase"><Plus className="h-4 w-4" /></button>
      </div>
      <button className="lc-btn-primary mt-3 w-full !py-3" onClick={() => toast("Demo only — one-off add-ons will be available once payments are enabled.")} data-testid="extras-order-button">
        Add to next delivery
      </button>
    </Card>
  );
};

export const SupportCard = () => (
  <Card title="Need help?" icon={LifeBuoy} testid="support-card">
    <p className="text-sm text-[#A1A1AA]">Our team is happy to help with anything about your subscription or deliveries.</p>
    <Link to="/contact" className="mt-4 inline-flex text-sm font-semibold text-[#FF6A00] hover:underline" data-testid="support-contact-link">Contact customer support</Link>
  </Card>
);
